import {
  formatAuthorTime,
  formatSpeedMph,
  parseAuthorTime,
  type ScenarioClockConfig,
} from './format'
import { storyAt, storyDeltaMs } from './storyTime'
import type { DisplayUnits, PoseKeyframe, WindKeyframe } from './types'

export type TornadoPresetId = 'graze' | 'touchdown' | 'stationary' | 'erratic'

export type TornadoPresetDef = {
  id: TornadoPresetId
  label: string
  /** Peak wind in the recipe. Shown with the name so a preset reads as intensity. */
  peakMph: number
  preferredOnsetHm?: string
  defaultSpanMs: number
}

export const TORNADO_PRESET_SPAN_OPTIONS: { ms: number; label: string }[] = [
  { ms: 30 * 60_000, label: '30 min' },
  { ms: 45 * 60_000, label: '45 min' },
  { ms: 60 * 60_000, label: '1 hour' },
  { ms: 90 * 60_000, label: '90 min' },
  { ms: 2 * 60 * 60_000, label: '2 hours' },
  { ms: 3 * 60 * 60_000, label: '3 hours' },
]

export const TORNADO_PRESETS: readonly TornadoPresetDef[] = [
  {
    id: 'graze',
    label: 'Graze',
    peakMph: 70,
    preferredOnsetHm: '10:15 AM',
    defaultSpanMs: 45 * 60_000,
  },
  {
    id: 'touchdown',
    label: 'Touchdown',
    peakMph: 120,
    preferredOnsetHm: '10:20 AM',
    defaultSpanMs: 60 * 60_000,
  },
  {
    id: 'stationary',
    label: 'Stationary',
    peakMph: 95,
    preferredOnsetHm: '10:00 AM',
    defaultSpanMs: 3 * 60 * 60_000,
  },
  {
    id: 'erratic',
    label: 'Erratic',
    peakMph: 130,
    preferredOnsetHm: '10:00 AM',
    defaultSpanMs: 90 * 60_000,
  },
]

export function tornadoPresetById(id: TornadoPresetId): TornadoPresetDef {
  return TORNADO_PRESETS.find((p) => p.id === id) ?? TORNADO_PRESETS[0]!
}

const TORNADO_PRESET_IDS = new Set<string>(TORNADO_PRESETS.map((p) => p.id))

/** Last type Apply wrote. Empty when this tornado has never had a type applied. */
export function tornadoPresetIdFromParams(params: Record<string, unknown>): TornadoPresetId | '' {
  const id = params.tornadoPresetId
  return typeof id === 'string' && TORNADO_PRESET_IDS.has(id) ? (id as TornadoPresetId) : ''
}

/** Duration chosen with that type. Null when it was never stored or is not a duration option. */
export function tornadoPresetSpanFromParams(params: Record<string, unknown>): number | null {
  const raw = params.presetSpanMs
  const ms = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN
  if (!Number.isFinite(ms)) return null
  return TORNADO_PRESET_SPAN_OPTIONS.some((opt) => opt.ms === ms) ? ms : null
}

export function suggestedTornadoPresetOnsetMs(
  preset: TornadoPresetDef,
  clock: ScenarioClockConfig,
  fallbackMs: number,
): number {
  if (preset.preferredOnsetHm) {
    const iso = parseAuthorTime(preset.preferredOnsetHm, clock)
    if (iso != null) return Math.max(0, storyDeltaMs(clock.startsAt, iso))
  }
  return Math.max(0, Math.round(fallbackMs))
}

export function formatTornadoPresetBlurb(
  id: TornadoPresetId,
  opts: {
    displayUnits: DisplayUnits
    clock: ScenarioClockConfig
    onsetMs: number
    spanMs: number
  },
): string {
  const start = formatAuthorTime(storyAt(opts.clock.startsAt, opts.onsetMs), opts.clock)
  const end = formatAuthorTime(
    storyAt(opts.clock.startsAt, opts.onsetMs + Math.max(0, opts.spanMs)),
    opts.clock,
  )
  const window = `${start} → ${end}`
  if (id === 'graze') {
    const peak = formatSpeedMph(70, opts.displayUnits)
    return `Sweeps east north of the drop point ${window}; wind follows that track, peak near ~${peak}, then dissipates.`
  }
  if (id === 'stationary') {
    const peak = formatSpeedMph(95, opts.displayUnits)
    return `Lingers near the drop point ${window}; wind turns with each jog, peak near ~${peak}.`
  }
  if (id === 'erratic') {
    const peak = formatSpeedMph(130, opts.displayUnits)
    return `Whips around the drop point ${window}; wind points along each jag, peak near ~${peak}.`
  }
  const peak = formatSpeedMph(120, opts.displayUnits)
  return `Starts at the drop point and tracks east ${window}; wind stays with the track, peak near ~${peak} mid-track, then clears.`
}

function clampDeg(deg: number): number {
  return ((Math.round(deg) % 360) + 360) % 360
}

function windFrame(
  originIso: string,
  atElapsedMs: number,
  fromDeg: number,
  speedMph: number,
  plumeDistanceM: number,
  plumeSpreadDeg: number,
): WindKeyframe {
  return {
    at: storyAt(originIso, Math.max(0, Math.round(atElapsedMs))),
    fromDeg: clampDeg(fromDeg),
    speedMph: Math.max(0, Math.round(speedMph)),
    plumeDistanceM: Math.max(50, Math.round(plumeDistanceM)),
    plumeSpreadDeg: Math.max(5, Math.min(90, Math.round(plumeSpreadDeg))),
  }
}

function atSpan(onsetMs: number, spanMs: number, u: number): number {
  return onsetMs + Math.round(Math.max(0, Math.min(1, u)) * Math.max(1, spanMs))
}

/** Offset meters → approx lng/lat delta at a reference latitude. */
function offsetLngLat(
  origin: { lng: number; lat: number },
  eastM: number,
  northM: number,
): { lng: number; lat: number } {
  const dLat = northM / 111_320
  const dLng = eastM / (111_320 * Math.cos((origin.lat * Math.PI) / 180))
  return { lng: origin.lng + dLng, lat: origin.lat + dLat }
}

function pathFrames(
  points: { lng: number; lat: number }[],
  us: number[],
  onset: number,
  span: number,
  originIso: string,
): PoseKeyframe[] {
  return points.map((p, i) => ({
    at: storyAt(originIso, atSpan(onset, span, us[i] ?? i / Math.max(1, points.length - 1))),
    lng: p.lng,
    lat: p.lat,
  }))
}

/** Drop points that sit within `minSepM` of an earlier kept point (demo clarity). */
function spacePathPoints(
  points: { lng: number; lat: number }[],
  minSepM: number,
): { lng: number; lat: number }[] {
  const kept: { lng: number; lat: number }[] = []
  for (const p of points) {
    const tooClose = kept.some((k) => {
      const dLat = (p.lat - k.lat) * 111_320
      const dLng = (p.lng - k.lng) * 111_320 * Math.cos((k.lat * Math.PI) / 180)
      return Math.hypot(dLat, dLng) < minSepM
    })
    if (!tooClose) kept.push(p)
  }
  return kept.length ? kept : points.slice(0, 1)
}

export type TornadoPresetBuild = {
  keyframes: PoseKeyframe[]
  windKeyframes: WindKeyframe[]
  params: {
    fromDeg: number
    speedMph: number
    plumeDistanceM: number
    plumeSpreadDeg: number
    showPlume: boolean
    /** Stable impact ring. Direction changes do not resize it. */
    radiusM: number
  }
}

/** Compass bearing of travel, degrees clockwise from north. Null when the step is too short to aim. */
function travelBearingDeg(
  from: { lng: number; lat: number },
  to: { lng: number; lat: number },
): number | null {
  const dLat = (to.lat - from.lat) * 111_320
  const dLng =
    (to.lng - from.lng) * 111_320 * Math.cos((((from.lat + to.lat) / 2) * Math.PI) / 180)
  if (Math.hypot(dLat, dLng) < 1) return null
  return clampDeg((Math.atan2(dLng, dLat) * 180) / Math.PI)
}

/**
 * Wind-from degrees so the direction wedge points along an east/north step.
 * One bearing for the whole ring — it follows the track, it does not spin.
 */
export function windFromOffset(eastM: number, northM: number, fallbackDeg: number): number {
  if (Math.hypot(eastM, northM) < 1) return clampDeg(fallbackDeg)
  const travel = clampDeg((Math.atan2(eastM, northM) * 180) / Math.PI)
  return clampDeg(travel + 180)
}

function speedAt(u: number, keys: readonly (readonly [number, number])[]): number {
  const uClamped = Math.max(0, Math.min(1, u))
  let i = 0
  while (i < keys.length - 1 && uClamped > keys[i + 1]![0]) i++
  const a = keys[i]!
  const b = keys[Math.min(keys.length - 1, i + 1)]!
  if (b[0] <= a[0]) return b[1]
  const t = (uClamped - a[0]) / (b[0] - a[0])
  return a[1] + (b[1] - a[1]) * Math.max(0, Math.min(1, t))
}

/** One wind sample per path point. Bearing is the leg ahead, so the wedge points where the tornado is going. */
function windAlongPath(
  points: { lng: number; lat: number }[],
  us: number[],
  onset: number,
  span: number,
  originIso: string,
  speeds: readonly (readonly [number, number])[],
  radiusM: number,
): WindKeyframe[] {
  let prevTravel = 90
  return points.map((_, i) => {
    const u = us[i] ?? (points.length <= 1 ? 0 : i / Math.max(1, points.length - 1))
    const fromPt = i < points.length - 1 ? points[i]! : points[Math.max(0, i - 1)]!
    const toPt = i < points.length - 1 ? points[i + 1]! : points[i]!
    const aimed = travelBearingDeg(fromPt, toPt)
    if (aimed != null) prevTravel = aimed
    return windFrame(originIso, atSpan(onset, span, u), prevTravel + 180, speedAt(u, speeds), radiusM, 28)
  })
}

function presetParams(windKeyframes: WindKeyframe[], radiusM: number): TornadoPresetBuild['params'] {
  const peak = windKeyframes.reduce((best, frame) => (frame.speedMph > best.speedMph ? frame : best))
  return {
    fromDeg: peak.fromDeg,
    speedMph: peak.speedMph,
    plumeDistanceM: radiusM,
    plumeSpreadDeg: 28,
    showPlume: true,
    radiusM,
  }
}

/** Stable rings. Graze stays short of a 1.2 km miss; erratic is tight so the drop enters and leaves. */
const PRESET_RADIUS_M: Record<TornadoPresetId, number> = {
  graze: 900,
  touchdown: 1200,
  stationary: 1000,
  erratic: 480,
}

/**
 * Build path + local-wind series for a tornado preset around `origin` (the drop pose).
 * Sweep presets use the drop as path #1; graze orbits north of it; stationary loops on it.
 * Local wind is one direction pointed along each leg of that path. The ring does not pulse.
 * Callers that know the story end should pass a window from `clampTornadoPresetWindow`.
 */
export function buildTornadoPreset(
  id: TornadoPresetId,
  opts: {
    origin: { lng: number; lat: number }
    onsetMs: number
    spanMs: number
    originIso: string
  },
): TornadoPresetBuild {
  const onset = Math.max(0, Math.round(opts.onsetMs))
  const span = Math.max(5 * 60_000, Math.round(opts.spanMs))
  const o = opts.origin

  if (id === 'graze') {
    // Arc north of the site — drop is the geographic anchor, not on the path.
    const points = [
      offsetLngLat(o, -1800, 900),
      offsetLngLat(o, -900, 1100),
      offsetLngLat(o, 0, 1200),
      offsetLngLat(o, 900, 1100),
      offsetLngLat(o, 1800, 800),
      offsetLngLat(o, 2400, 400),
    ]
    const us = [0, 0.2, 0.4, 0.55, 0.8, 1]
    const keyframes = pathFrames(points, us, onset, span, opts.originIso)
    const radiusM = PRESET_RADIUS_M.graze
    const windKeyframes = windAlongPath(
      points,
      us,
      onset,
      span,
      opts.originIso,
      [
        [0, 25],
        [0.4, 70],
        [1, 12],
      ],
      radiusM,
    )
    return { keyframes, windKeyframes, params: presetParams(windKeyframes, radiusM) }
  }

  if (id === 'stationary') {
    // Linger near the drop — wander centered on origin (#1 = drop). Do not close the
    // loop on the exact start (stacked 1/N pins); keep peers ≥ ~180 m apart.
    const raw: { eastM: number; northM: number }[] = [
      { eastM: 0, northM: 0 },
      { eastM: 280, northM: 420 },
      { eastM: 520, northM: -80 },
      { eastM: 120, northM: -480 },
      { eastM: -360, northM: -320 },
      { eastM: -480, northM: 160 },
      { eastM: -80, northM: 440 },
      { eastM: 360, northM: 200 },
      { eastM: 200, northM: -280 },
      { eastM: -200, northM: 120 },
    ]
    const points = spacePathPoints(
      raw.map((p) =>
        p.eastM === 0 && p.northM === 0 ? { lng: o.lng, lat: o.lat } : offsetLngLat(o, p.eastM, p.northM),
      ),
      180,
    )
    const us = points.map((_, i) => (points.length <= 1 ? 0 : i / (points.length - 1)))
    const keyframes = pathFrames(points, us, onset, span, opts.originIso)
    const radiusM = PRESET_RADIUS_M.stationary
    const windKeyframes = windAlongPath(
      points,
      us,
      onset,
      span,
      opts.originIso,
      [
        [0, 70],
        [4 / 9, 95],
        [1, 40],
      ],
      radiusM,
    )
    return { keyframes, windKeyframes, params: presetParams(windKeyframes, radiusM) }
  }

  if (id === 'erratic') {
    // Tight, jagged wander. Local wind points along each jag, so the direction mark turns with the track.
    const raw: { eastM: number; northM: number }[] = [
      { eastM: 0, northM: 0 },
      { eastM: 420, northM: 180 },
      { eastM: -120, northM: 480 },
      { eastM: -520, northM: 40 },
      { eastM: -200, northM: -420 },
      { eastM: 360, northM: -380 },
      { eastM: 560, northM: 80 },
      { eastM: 80, northM: 360 },
      { eastM: -340, northM: 220 },
      { eastM: 160, northM: -160 },
    ]
    const points = spacePathPoints(
      raw.map((p) =>
        p.eastM === 0 && p.northM === 0 ? { lng: o.lng, lat: o.lat } : offsetLngLat(o, p.eastM, p.northM),
      ),
      180,
    )
    const us = points.map((_, i) => (points.length <= 1 ? 0 : i / (points.length - 1)))
    const keyframes = pathFrames(points, us, onset, span, opts.originIso)
    const radiusM = PRESET_RADIUS_M.erratic
    const windKeyframes = windAlongPath(
      points,
      us,
      onset,
      span,
      opts.originIso,
      [
        [0, 55],
        [0.375, 130],
        [0.5, 70],
        [0.75, 115],
        [1, 25],
      ],
      radiusM,
    )
    return { keyframes, windKeyframes, params: presetParams(windKeyframes, radiusM) }
  }

  // Touchdown — drop is path #1, then tracks east through the area.
  const points = [
    { lng: o.lng, lat: o.lat },
    offsetLngLat(o, 700, -80),
    offsetLngLat(o, 1400, -180),
    offsetLngLat(o, 2200, -320),
    offsetLngLat(o, 3000, -480),
    offsetLngLat(o, 3800, -620),
  ]
  const us = [0, 0.18, 0.38, 0.58, 0.8, 1]
  const keyframes = pathFrames(points, us, onset, span, opts.originIso)
  const radiusM = PRESET_RADIUS_M.touchdown
  const windKeyframes = windAlongPath(
    points,
    us,
    onset,
    span,
    opts.originIso,
    [
      [0, 35],
      [0.38, 120],
      [1, 8],
    ],
    radiusM,
  )
  return { keyframes, windKeyframes, params: presetParams(windKeyframes, radiusM) }
}

/**
 * Keep the onset the author set (drop clock or a manual edit).
 * If the duration would run past the story end, shorten the duration.
 */
export function clampTornadoPresetWindow(
  onsetMs: number,
  spanMs: number,
  clockEndMs: number,
): { onsetMs: number; spanMs: number } {
  const end = Math.max(1, Math.round(clockEndMs))
  const minSpan = 60_000
  let onset = Math.round(onsetMs)
  if (!Number.isFinite(onset) || onset < 0) onset = 0
  if (onset > end) onset = end
  let span = Math.max(minSpan, Math.round(spanMs))
  const room = end - onset
  if (room > 0 && span > room) span = Math.max(minSpan, room)
  if (onset + span > end && room > 0) span = room
  return { onsetMs: onset, spanMs: Math.max(1, span) }
}

/** Convenience: clamp then build. */
export function buildClampedTornadoPreset(
  id: TornadoPresetId,
  opts: {
    origin: { lng: number; lat: number }
    onsetMs: number
    spanMs: number
    clockEndMs: number
    originIso: string
  },
): TornadoPresetBuild & { onsetMs: number; spanMs: number } {
  const clamped = clampTornadoPresetWindow(opts.onsetMs, opts.spanMs, opts.clockEndMs)
  const built = buildTornadoPreset(id, {
    origin: opts.origin,
    onsetMs: clamped.onsetMs,
    spanMs: clamped.spanMs,
    originIso: opts.originIso,
  })
  return { ...built, onsetMs: clamped.onsetMs, spanMs: clamped.spanMs }
}
